import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { FC, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { NavigationLink, type NavigationItemProps, type NavigationProps } from "@tennr/frontend/components/Navigation";
import { Button } from "@tennr/frontend/components/Button";
import { AnimatedLogo } from "@tennr/frontend/components/AnimatedLogo";
import { useBackgroundColor } from "@tennr/frontend/context";
import { checkLightBackground } from "@tennr/frontend/utils";
import { useThemeColor } from "@tennr/frontend/hooks/useThemeColor";
import "./MobileNavigation.css";

interface MobileNavigationProps extends NavigationProps {
  hamburgerColor?: string;
  hamburgerColorOpen?: string;
  drawerBackground?: string;
  drawerTextColor?: string;
  onNavigate?: (page: string) => void;
  draggable?: boolean;
}

const DEFAULT_BROWN = "#5F4037"; // Default brown color for the drawer background / hamburger dark color

const MobileNavigationItem: FC<NavigationItemProps> = ({ item, active, className = "", onClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      {item.subitems ? (
        <div className={`nav-submenu ${isMenuOpen ? "open" : ""}`}>
          <button
            className={`nav-item ${!isMenuOpen && item.subitems.some((item) => item.to === location.pathname) ? "active" : ""} ${className}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span>{item.title}</span>
            <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.12891 18.8711L10 10L1.12891 1.12891" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <div className="nav-submenu-secondary-content">
            <button className="mobile-navigation-back" onClick={() => setIsMenuOpen(false)}>
              <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.1289 1.12891L2.25781 10L11.1289 18.8711" stroke="currentColor" strokeWidth="2" />
              </svg>
              <span>{item.title}</span>
            </button>
            <ul className="nav-items nav-items-secondary">
              {item.subitems.map((subitem, i) => {
                return (
                  <li key={i}>
                    <NavigationLink
                      item={subitem}
                      active={!!subitem.to && location.pathname === subitem.to}
                      disableDynamicText={true}
                      className={className}
                      onClick={onClick}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : (
        <NavigationLink item={item} active={active} disableDynamicText={true} className={className} onClick={onClick} />
      )}
    </>
  );
};

export const MobileNavigation: FC<MobileNavigationProps> = ({
  navigationItems,
  transparent,
  hamburgerColorOpen = "#EEDFD1",
  drawerBackground = DEFAULT_BROWN,
  drawerTextColor = "#EEDFD1",
  draggable = false,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [, setIsDraggingHandle] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const { currentColor } = useBackgroundColor();
  const isLightBackground = checkLightBackground(currentColor);

  // Update theme color when drawer is open
  useThemeColor(
    isDrawerOpen
      ? {
          color: drawerBackground,
          statusBarStyle: "white", // nav is brown so it needs white text
        }
      : undefined
  );

  // Prevent page scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) document.documentElement.classList.add("scroll-lock-below-desktop");

    return () => {
      document.documentElement.classList.remove("scroll-lock-below-desktop");
    };
  }, [isDrawerOpen]);

  const closeDrawer = () => setIsDrawerOpen(false);
  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);

  const location = useLocation();

  const topLineVariants = {
    closed: { rotate: 0, y: 0, backgroundColor: isLightBackground ? DEFAULT_BROWN : hamburgerColorOpen },
    open: { rotate: 45, y: 9, backgroundColor: hamburgerColorOpen },
  };

  const middleLineVariants = {
    closed: { scaleX: 1, opacity: 1, backgroundColor: isLightBackground ? DEFAULT_BROWN : hamburgerColorOpen },
    open: { scaleX: 0, opacity: 0, backgroundColor: hamburgerColorOpen },
  };

  const bottomLineVariants = {
    closed: { rotate: 0, y: 0, backgroundColor: isLightBackground ? DEFAULT_BROWN : hamburgerColorOpen },
    open: { rotate: -45, y: -9, backgroundColor: hamburgerColorOpen },
  };

  const drawerVariants = {
    closed: {
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
    open: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  // fade out navigation over 100vh with custom easing (slow start, fast end)
  const { scrollYProgress } = useScroll({ offset: ["start start", "100vh start"] });
  const navigationOpacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [1, 0.9, 0.3, 0], {
    ease: (t) => 1 - Math.pow(1 - t, 3), // Cubic ease-out for smooth acceleration
  });

  return (
    <nav
      className={`navigation mobile-navigation ${transparent ? "transparent" : ""} ${isDrawerOpen ? "open" : ""}`}
      onKeyDown={(e) => e.code === "Escape" && closeDrawer()}
    >
      <div className="mobile-navigation-header">
        <Link to="/" aria-label="Tennr" className="nav-logo-link" onClick={closeDrawer}>
          <motion.div
            className="nav-logo"
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            style={{ opacity: isDrawerOpen ? 1 : navigationOpacity }}
          >
            <AnimatedLogo
              width={120}
              height={30}
              isLightBackground={isDrawerOpen ? false : isLightBackground}
              opacity={0.9}
              enableScrollEffects={false}
            />
          </motion.div>
        </Link>

        {/* Mobile Hamburger Button */}
        <motion.button
          ref={hamburgerRef}
          className="hamburger-button"
          onClick={toggleDrawer}
          initial="closed"
          animate={isDrawerOpen ? "open" : "closed"}
          aria-label={isDrawerOpen ? "Close menu" : "Open menu"}
        >
          <motion.span
            className="hamburger-line top-line"
            variants={topLineVariants}
            transition={{
              rotate: { duration: 0.3, ease: "easeInOut" },
              y: { duration: 0.3, ease: "easeInOut" },
              backgroundColor: { duration: 0 },
            }}
          />
          <motion.span
            className="hamburger-line middle-line"
            variants={middleLineVariants}
            transition={{
              scaleX: { duration: 0.2, ease: "easeInOut" },
              opacity: { duration: 0.2, ease: "easeInOut" },
              backgroundColor: { duration: 0 },
            }}
          />
          <motion.span
            className="hamburger-line bottom-line"
            variants={bottomLineVariants}
            transition={{
              rotate: { duration: 0.3, ease: "easeInOut" },
              y: { duration: 0.3, ease: "easeInOut" },
              backgroundColor: { duration: 0 },
            }}
          />
        </motion.button>
      </div>

      <AnimatePresence>
        {isDrawerOpen && (
          <motion.div
            className="mobile-navigation-drawer"
            initial="closed"
            animate="open"
            exit="closed"
            variants={drawerVariants}
            ref={drawerRef}
            drag={draggable ? "y" : false}
            dragConstraints={draggable ? { top: 0, bottom: 0 } : undefined}
            dragElastic={draggable ? { top: 0.2, bottom: 0 } : undefined}
            onDragStart={
              draggable
                ? () => {
                    setIsDragging(true);
                  }
                : undefined
            }
            onDrag={
              draggable
                ? (_, info) => {
                    // update dragY for visual feedback, only track negative values (upward drag)
                    if (info.offset.y < 0) {
                      setDragY(info.offset.y);
                    } else {
                      setDragY(0);
                    }
                  }
                : undefined
            }
            onDragEnd={
              draggable
                ? (_, info) => {
                    // close if dragged up with enough velocity or distance
                    if (info.offset.y < -150 || info.velocity.y < -500) {
                      setIsDrawerOpen(false);
                    }
                    // reset drag position
                    setDragY(0);
                    setIsDragging(false);
                    setIsDraggingHandle(false);
                  }
                : undefined
            }
            style={{
              backgroundColor: drawerBackground,
              opacity: draggable && isDragging && dragY < 0 ? Math.max(0.1, 1 + dragY / 200) : 1,
              y: draggable && isDragging && dragY < 0 ? dragY : 0,
              cursor: draggable ? "default" : undefined,
            }}
          >
            <img src="/svg/footer-bg.svg" alt="" className="mobile-navigation-drawer-background" draggable={false} />
            {/* Drag indicator - only show if draggable is enabled */}
            {draggable && (
              <motion.div
                className="drag-indicator-container"
                animate={{
                  opacity: dragY < 0 ? 1 : 0.7,
                }}
                ref={handleRef}
                onPointerDown={() => {
                  setIsDraggingHandle(true);
                }}
                onPointerUp={() => {
                  setIsDraggingHandle(false);
                }}
                onPointerCancel={() => {
                  setIsDraggingHandle(false);
                }}
              >
                <motion.div
                  className="drag-indicator"
                  style={{
                    width: `${Math.min(80, 40 - dragY / 2)}px`,
                    backgroundColor: dragY < -30 ? "rgba(255, 255, 255, 0.8)" : "rgba(255, 255, 255, 0.3)",
                    cursor: isDragging ? "grabbing" : "grab",
                    transition: isDragging ? "none" : "width 0.2s, background-color 0.2s",
                  }}
                />
                <motion.div
                  className="drag-hint"
                  style={{
                    opacity: dragY < -60 ? 1 : 0,
                    transition: "opacity 0.1s",
                  }}
                >
                  Release to close
                </motion.div>
              </motion.div>
            )}
            <ul className="nav-items">
              <li>
                <MobileNavigationItem
                  item={{ title: "Home", to: "/" }}
                  active={location.pathname === "/"}
                  className="heading-lg"
                  onClick={closeDrawer}
                />
              </li>
              {navigationItems.map((item, i) => {
                return (
                  <li key={i}>
                    <MobileNavigationItem
                      item={item}
                      active={!!item.to && location.pathname === item.to}
                      className="heading-lg"
                      onClick={closeDrawer}
                    />
                  </li>
                );
              })}
            </ul>
            <Link to="/book" style={{ textDecoration: "none" }} className="mobile-navigation-cta" onClick={closeDrawer}>
              <Button
                minWidth={"100%"}
                text="BOOK A DEMO"
                background={drawerTextColor}
                textFromColor={drawerBackground}
              />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
